// P4 — Receipt detail page.
//
// "View receipt" links on the Queue + schedule rows historically
// pointed at /brand-os/publish/receipts/{id} but no route existed →
// 404 in prod. This page renders the immutable
// bos_publish_receipts row for one receipt_id, including:
//
//   • published_at + execution_status (success / failed)
//   • the external artifact (id + url)
//   • the governance snapshot (preflight + Guardian rule-set version)
//   • the connection fingerprint
//   • the Reel cut shipped (P4 — cut_key + source + reason)
//   • the failure envelope if execution_status != "published"
//
// It stays faithful to the "receipt is immutable truth" rule — no
// mutations happen here.
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDateTimeTz, useOrgTimezone, channelDisplay } from "../lib/tz";

function fmtDate(d) {
  if (!d) return "—";
  return fmtDateTimeTz(d);
}

function Section({ title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bo-card"
      style={{ padding: 16, display: "grid", gap: 10 }}
    >
      <div className="bo-eyebrow" style={{ fontSize: 11 }}>{title}</div>
      {children}
    </section>
  );
}

function KV({ label, value, testId, mono = false }) {
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>{label}</div>
      <div
        data-testid={testId}
        className={mono ? "bo-mono" : ""}
        style={{ fontSize: 13, wordBreak: "break-word" }}
      >
        {value == null || value === "" ? "—" : value}
      </div>
    </div>
  );
}

export default function PublishReceiptDetail() {
  const { receiptId } = useParams();
  // Ensures the org tz is loaded once per page view so fmtDate renders
  // in the right zone even on a hard refresh.
  useOrgTimezone();
  const [receipt, setReceipt] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // P4b — delete-from-platform state. Confirm dialog + call status
  // are owner-only and gated behind the receipt's channel policy.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOutcome, setDeleteOutcome] = useState(null);
  // Round 22 — re-point the live post's CTA link to the waitlist.
  const [repointing, setRepointing] = useState(false);
  const [repointOutcome, setRepointOutcome] = useState(null);

  async function onRepointCta() {
    setRepointing(true);
    setRepointOutcome(null);
    try {
      const r = await api.post(`/publish-receipts/${receiptId}/repoint-cta`);
      setRepointOutcome(r.data);
      await reload();
    } catch (e) {
      setRepointOutcome({
        status: "error",
        message: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message,
      });
    } finally {
      setRepointing(false);
    }
  }

  async function reload() {
    try {
      const r = await api.get(`/publish-receipts/${receiptId}`);
      setReceipt(r.data?.receipt || r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || { message: e.message });
    }
  }

  useEffect(() => {
    (async () => {
      setErr(null);
      setLoading(true);
      try {
        const r = await api.get(`/publish-receipts/${receiptId}`);
        setReceipt(r.data?.receipt || r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || { message: e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [receiptId]);

  async function onDelete() {
    setDeleting(true);
    setDeleteOutcome(null);
    try {
      const r = await api.post(
        `/publish-receipts/${receiptId}/delete-from-platform`,
      );
      setDeleteOutcome(r.data);
      await reload();
    } catch (e) {
      setDeleteOutcome({
        status: "error",
        message: e?.response?.data?.detail?.message || e.message,
      });
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  }

  if (loading) {
    return (
      <div className="bo-page" data-testid="publish-receipt-detail-loading">
        <section className="bo-card" style={{ padding: 16 }}>
          Loading receipt…
        </section>
      </div>
    );
  }

  if (err) {
    return (
      <div className="bo-page" data-testid="publish-receipt-detail-error">
        <section className="bo-card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, color: "crimson" }}>
            {err.code || "Receipt not found"}
          </div>
          <div style={{ fontSize: 13 }}>{err.message || String(err)}</div>
          <div style={{ marginTop: 10 }}>
            <Link to="/brand-os/publish/queue" className="bo-link-sm">
              ← Back to queue
            </Link>
          </div>
        </section>
      </div>
    );
  }

  if (!receipt) return null;

  const status = (receipt.execution_status || "").toLowerCase();
  const statusTone = status === "published" ? "default"
    : status === "failed" ? "destructive" : "secondary";

  const cut = receipt.reel_cut_shipped || null;
  const connFp = receipt.channel_connection_fingerprint;

  return (
    <div className="bo-page" data-testid="publish-receipt-detail-page">
      <section
        className="bo-card"
        data-testid="publish-receipt-detail-header"
        style={{ padding: 16, display: "grid", gap: 10 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={{ margin: 0, fontSize: 20 }}>Publish Receipt</h1>
          <Badge
            variant={statusTone}
            data-testid="receipt-status-badge"
          >
            {receipt.execution_status || "—"}
          </Badge>
        </div>
        <div className="bo-mono" style={{ fontSize: 12,
              color: "var(--bo-muted)" }}>
          <span data-testid="receipt-id">{receipt.id}</span>
          {receipt.publish_plan_id && (
            <> · plan <span data-testid="receipt-plan-id">
              {receipt.publish_plan_id.slice(0, 8)}
            </span></>
          )}
        </div>
        <div>
          <Link to="/brand-os/publish/queue" className="bo-link-sm">
            ← Back to queue
          </Link>
        </div>
      </section>

      {/* P4b — Delete from platform. Owner-only. Confirm dialog
          because acceptance posts might otherwise be deleted twice
          or by mistake. If the channel is manual-only (IG + TikTok)
          we still render the button but it just surfaces the native-
          app instructions on click.

          P5a (2026-11-02) — hide the entire section when there is
          nothing to delete: no external_artifact_id AND no prior
          delete record. Previously this showed on FAILED receipts
          with no artifact (e.g. 43622e9d) and misled the operator
          into thinking cleanup was pending. */}
      {receipt.execution_status === "published" && receipt.external_artifact_id && (
      <Section title="CTA link · waitlist" testId="receipt-cta-repoint">
        <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--bo-ink-soft)" }}>
          Re-point the link in this live post's caption to the Precipice Health
          waitlist. Facebook is edited through the API; Instagram and YouTube
          report exactly what to do by hand. Every attempt writes an edit receipt.
        </div>
        {receipt.cta_edit && (
          <div data-testid="receipt-cta-edit-status" className="bo-mono"
               style={{ fontSize: 12, color: "var(--bo-muted)" }}>
            last edit: {receipt.cta_edit.status} · {fmtDate(receipt.cta_edit.at)} · edit receipt{" "}
            {(receipt.cta_edit.edit_receipt_id || "").slice(0, 8)}
          </div>
        )}
        <Button
          data-testid="receipt-repoint-cta-btn"
          onClick={onRepointCta}
          disabled={repointing}
          style={{ alignSelf: "flex-start" }}
        >
          {repointing ? "Re-pointing…" : "Re-point CTA to waitlist"}
        </Button>
        {repointOutcome && (
          <div
            data-testid="receipt-repoint-outcome"
            style={{
              padding: "8px 10px", borderRadius: 6, fontSize: 13, lineHeight: 1.5,
              background: repointOutcome.status === "edited" ? "var(--bo-success-soft, #edf7ee)"
                : repointOutcome.status === "failed" || repointOutcome.status === "error"
                  ? "var(--bo-danger-soft, #fdecec)" : "var(--bo-panel-alt, #faf8f2)",
            }}
          >
            <strong>{repointOutcome.status}</strong>
            {repointOutcome.manual_help && <div style={{ marginTop: 4 }}>{repointOutcome.manual_help}</div>}
            {repointOutcome.message && <div style={{ marginTop: 4 }}>{String(repointOutcome.message)}</div>}
            {repointOutcome.new_text && repointOutcome.status !== "no_change" && (
              <pre style={{ marginTop: 6, whiteSpace: "pre-wrap", fontSize: 12 }}
                   data-testid="receipt-repoint-new-text">{repointOutcome.new_text}</pre>
            )}
          </div>
        )}
      </Section>
      )}

      {(receipt.external_artifact_id || receipt.deleted_at) && (
      <Section title="Acceptance cleanup" testId="receipt-cleanup">
        {receipt.deleted_at ? (
          <div
            data-testid="receipt-deleted-banner"
            style={{
              padding: "8px 10px", borderRadius: 6,
              background: "var(--bo-panel-alt, #faf8f2)",
              fontSize: 13, lineHeight: 1.5,
            }}
          >
            <strong>Platform delete recorded.</strong>{" "}
            Status:{" "}
            <code data-testid="receipt-delete-status">
              {receipt.platform_delete_status || "unknown"}
            </code>{" "}
            · at <span data-testid="receipt-deleted-at">
              {fmtDate(receipt.deleted_at)}
            </span>
            {receipt.platform_delete_manual_help && (
              <div style={{ marginTop: 4, fontSize: 12,
                            color: "var(--bo-muted)" }}>
                {receipt.platform_delete_manual_help}
              </div>
            )}
          </div>
        ) : (receipt.delete_policy === "manual_only"
             && /^facebook:/.test(receipt.delete_surface_key || "")) ? (
          /* publish-86 round-10 · item B — Facebook Page stories have no
             Graph DELETE (code 100 / subcode 33). Hide the button and
             show the manual-only note; stories expire after 24h. */
          <div
            data-testid="receipt-delete-manual-only"
            style={{
              padding: "8px 10px", borderRadius: 6,
              background: "var(--bo-panel-alt, #faf8f2)",
              fontSize: 13, lineHeight: 1.5,
            }}
          >
            <strong>Manual-only cleanup.</strong>{" "}
            {receipt.delete_manual_help
              || "This surface can't be deleted through the API; it expires on its own after 24 hours."}
            {receipt.provider_metadata?.story_id && (
              <div style={{ marginTop: 4, fontSize: 12, color: "var(--bo-muted)" }}>
                story id <code data-testid="receipt-story-id">{receipt.provider_metadata.story_id}</code>
                {receipt.provider_metadata.source_media_id && (
                  <> · source media <code data-testid="receipt-source-media-id">{receipt.provider_metadata.source_media_id}</code></>
                )}
              </div>
            )}
          </div>
        ) : !confirmOpen ? (
          <Button
            data-testid="receipt-delete-btn"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={!receipt.external_artifact_id
                      && !(receipt.channel || "").match(/instagram|tiktok/i)}
            style={{ alignSelf: "flex-start" }}
          >
            Delete from platform
          </Button>
        ) : (
          <div
            data-testid="receipt-delete-confirm"
            style={{
              padding: 12, borderRadius: 8,
              border: "1px solid var(--bo-danger, #a0241b)",
              background: "var(--bo-danger-soft, #fdecec)",
              display: "grid", gap: 10,
            }}
          >
            <div style={{ fontWeight: 600 }}>
              Delete this post from{" "}
              <span data-testid="receipt-delete-channel">
                {channelDisplay(receipt.channel)}
              </span>?
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              This action is irreversible on the platform. The receipt
              itself stays as an immutable audit record; the delete
              outcome is appended to it.
              {(receipt.channel || "").match(/^instagram/i) && (
                <> This channel does not support API delete — we'll
                   mark the receipt as manual-only and link out.</>
              )}
              {(receipt.channel || "").match(/^tiktok/i) && (
                <> TikTok has no delete API — this will mark the
                   receipt manual-only and point you at the app.</>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                data-testid="receipt-delete-confirm-btn"
                variant="destructive"
                onClick={onDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Yes, delete"}
              </Button>
              <Button
                data-testid="receipt-delete-cancel-btn"
                variant="secondary"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
            </div>
            {deleteOutcome && (
              <div
                data-testid="receipt-delete-outcome"
                style={{ fontSize: 12,
                         color: deleteOutcome.status === "error"
                           ? "var(--bo-danger)"
                           : "var(--bo-ink-soft)" }}
              >
                <strong>{deleteOutcome.status}:</strong>{" "}
                {deleteOutcome.manual_help
                  || deleteOutcome.message
                  || "Done."}
              </div>
            )}
          </div>
        )}
      </Section>
      )}

      <Section title="Destination" testId="receipt-destination">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 10 }}>
          <KV label="Channel" value={channelDisplay(receipt.channel)}
              testId="receipt-channel" />
          <KV label="Published at" value={fmtDate(receipt.published_at)}
              testId="receipt-published-at" />
          <KV label="External artifact id"
              value={receipt.external_artifact_id}
              mono testId="receipt-external-artifact-id" />
          <KV label="External artifact url"
              value={receipt.external_artifact_url
                ? <a href={receipt.external_artifact_url}
                     target="_blank" rel="noopener noreferrer"
                     data-testid="receipt-external-artifact-link">
                    {receipt.external_artifact_url}
                  </a>
                : "—"} />
          <KV label="Connection id"
              value={receipt.channel_connection_id}
              mono testId="receipt-connection-id" />
          <KV label="Secret fingerprint"
              value={connFp} mono testId="receipt-secret-fingerprint" />
        </div>
      </Section>

      {cut && cut.cut_key && (
        <Section title="Reel cut shipped" testId="receipt-reel-cut">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 10 }}>
            <KV label="Cut key" value={cut.cut_key}
                testId="receipt-cut-key" />
            <KV label="Source" value={cut.source}
                testId="receipt-cut-source" />
            <KV label="Surface" value={cut.surface}
                testId="receipt-cut-surface" />
            <KV label="Reason" value={cut.reason}
                testId="receipt-cut-reason" />
            <KV label="Video URL"
                value={cut.video_url
                  ? <a href={cut.video_url} target="_blank"
                       rel="noopener noreferrer"
                       data-testid="receipt-cut-video-url">
                      {cut.video_url}
                    </a>
                  : "—"} />
          </div>
        </Section>
      )}

      <Section title="Governance snapshot" testId="receipt-governance">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 10 }}>
          <KV label="Campaign id" value={receipt.campaign_id}
              mono testId="receipt-campaign-id" />
          <KV label="Campaign version id"
              value={receipt.campaign_version_id} mono />
          <KV label="Asset set id" value={receipt.asset_set_id}
              mono testId="receipt-asset-set-id" />
          <KV label="Guardian rule-set version"
              value={receipt.guardian_rule_set_version_at_publish}
              mono testId="receipt-guardian-rsv" />
          <KV label="Package SHA-256"
              value={receipt.package_sha256
                ? receipt.package_sha256.slice(0, 16) + "…"
                : "—"} mono />
          <KV label="Payload SHA-256"
              value={receipt.payload_sha256
                ? receipt.payload_sha256.slice(0, 16) + "…"
                : "—"} mono />
        </div>
      </Section>

      {receipt.failure_envelope && (
        <Section title="Failure envelope" testId="receipt-failure">
          <div style={{
            padding: 10, borderRadius: 6,
            background: "var(--bo-danger-soft, #fdecec)",
            color: "var(--bo-danger, #a0241b)",
            fontSize: 13, lineHeight: 1.5,
            whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}>
            <strong>{receipt.failure_envelope.code || "failed"}:</strong>{" "}
            {receipt.failure_envelope.message || ""}
            {receipt.failure_envelope.provider_error && (
              <div className="bo-mono" style={{ marginTop: 8, fontSize: 11,
                    opacity: 0.85 }}>
                {receipt.failure_envelope.provider_error}
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Raw receipt" testId="receipt-raw">
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>
            Full JSON payload (read-only)
          </summary>
          <pre
            data-testid="receipt-raw-json"
            style={{
              marginTop: 8, fontSize: 11, padding: 10,
              background: "var(--bo-panel-alt, #fafaf6)",
              border: "1px solid var(--bo-line)",
              borderRadius: 6, overflow: "auto",
            }}
          >{JSON.stringify(receipt, null, 2)}</pre>
        </details>
      </Section>
    </div>
  );
}
